import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '@context/AuthContext';
import { ROUTES, API_ENDPOINTS } from '@utils/constants';
import Layout from '@components/layout/Layout';
import api from '@services/api';

type VerifyStatus = 'processing' | 'success' | 'error';

const MagicLinkVerifyPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithTokens, isAuthenticated, user } = useAuth();
  const [status, setStatus] = useState<VerifyStatus>('processing');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const verificationAttempted = React.useRef(false);

  useEffect(() => {
    const verifyMagicLink = async () => {
      const token = searchParams.get('token');
      const email = searchParams.get('email');

      if (!token || !email) {
        setStatus('error');
        setErrorMessage('Invalid magic link. Please request a new one.');
        return;
      }

      try {
        // Verify the magic link token
        const response = await api.post(API_ENDPOINTS.AUTH_MAGIC_LINK_VERIFY, {
          token,
          email: decodeURIComponent(email),
        });

        // Backend returns { message, user, tokens } at the top level. Tolerate an
        // optional { data: ... } envelope from other flows.
        const body = response.data?.data ?? response.data;
        const tokens = body?.tokens;
        const verifiedUser = body?.user;

        if (!tokens?.access_token || !tokens?.refresh_token) {
          throw new Error('Invalid response from server');
        }

        // Authenticate with the tokens and user data
        // Pass user data directly to avoid race condition with /auth/me call
        await loginWithTokens(tokens.access_token, tokens.refresh_token, verifiedUser);
        setStatus('success');

        const destination =
          verifiedUser?.role === 'photographer' ? ROUTES.DASHBOARD : ROUTES.HOME;
        setTimeout(() => {
          navigate(destination, { replace: true });
        }, 1500);
      } catch (err: any) {
        setStatus('error');
        const message = err.response?.data?.detail || err.message || 'Magic link verification failed';
        setErrorMessage(message);
      }
    };

    // Only process if not already authenticated and not already attempted
    if (!isAuthenticated && !verificationAttempted.current) {
      verificationAttempted.current = true;
      verifyMagicLink();
    } else if (isAuthenticated && !verificationAttempted.current) {
      // Already authenticated, redirect by role
      navigate(user?.role === 'photographer' ? ROUTES.DASHBOARD : ROUTES.HOME, {
        replace: true,
      });
    }
  }, [searchParams, loginWithTokens, navigate, isAuthenticated, user]);

  return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            {status === 'processing' && (
              <div className="text-center">
                <Loader2 className="mx-auto h-12 w-12 text-primary-600 animate-spin" />
                <h2 className="mt-4 text-xl font-semibold text-gray-900">
                  Verifying magic link...
                </h2>
                <p className="mt-2 text-gray-600">
                  Please wait while we sign you in.
                </p>
              </div>
            )}

            {status === 'success' && (
              <div className="text-center">
                <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
                <h2 className="mt-4 text-xl font-semibold text-gray-900">
                  Welcome!
                </h2>
                <p className="mt-2 text-gray-600">
                  You have been successfully signed in. Redirecting...
                </p>
              </div>
            )}

            {status === 'error' && (
              <div className="text-center">
                <XCircle className="mx-auto h-12 w-12 text-red-500" />
                <h2 className="mt-4 text-xl font-semibold text-gray-900">
                  Verification Failed
                </h2>
                <p className="mt-2 text-gray-600">
                  {errorMessage || 'The magic link is invalid or has expired.'}
                </p>
                <div className="mt-6 space-y-3">
                  <button
                    onClick={() => navigate(ROUTES.LOGIN, { replace: true })}
                    className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Return to Login
                  </button>
                  <p className="text-sm text-gray-500">
                    Magic links expire after 15 minutes. Request a new one from the login page.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default MagicLinkVerifyPage;
